import { useState } from 'react';

import useSWRInfinite from 'swr/infinite';

// Utils
import { get } from '@/utils/client';

// API Endpoints
import { GROUP_MEDIAS, MEDIAS } from '@/lib/routes/apiEndpoints';

/**
 * Check if we have at least one valid ID to make an API request
 * @returns {boolean} True if at least one ID is valid
 */
const hasValidID = (groupID, activeSPID, competitorID) => {
    return [groupID, activeSPID, competitorID].some((id) => id != null && id !== undefined);
};

const getKey = (
    pageIndex,
    previousPageData,
    activeSPID,
    limit,
    requestParams,
    endpoint = MEDIAS,
    competitorID,
    isCompetitorRequest,
    groupID
) => {
    // No request params
    if (!requestParams || (!competitorID && isCompetitorRequest)) return null;

    // Validate that we have at least one valid ID to make the request
    if (!hasValidID(groupID, activeSPID, competitorID)) return null;

    // Reached the end
    if (previousPageData && !previousPageData.medias) return null;

    // Determine the endpoint based on whether we're fetching for a group or profile
    let finalEndpoint;
    let finalRequestParams = requestParams;

    if (groupID && endpoint === MEDIAS) {
        finalEndpoint = GROUP_MEDIAS.replace('%GROUP_ID%', groupID);
    } else {
        finalEndpoint = endpoint.replace('%SPID%', competitorID ?? activeSPID);
    }

    // First page, we don't have `previousPageData`
    if (pageIndex === 0) {
        return [`${finalEndpoint}?per_page=${limit}`, JSON.stringify(finalRequestParams)];
    }

    // Pagination for groups (next_page_url)
    if (previousPageData.next_page_url) {
        return [
            `${finalEndpoint}?per_page=${limit}&page=${previousPageData.current_page + 1}`,
            JSON.stringify(finalRequestParams)
        ];
    }

    // Pagination for profiles (cursor)
    return [
        `${finalEndpoint}?per_page=${limit}&cursor=${previousPageData.cursor}`,
        JSON.stringify(finalRequestParams)
    ];
};

const useMedias = ({
    activeSPID = null,
    groupID = null,
    limit = 20,
    params = null,
    options = {},
    endpoint = undefined,
    isCompetitorRequest = false,
    competitorID = null
} = {}) => {
    const [requestParams, setRequestParams] = useState(params);
    const isFirstStatsLoaded = true;

    const {
        data: dataMedias,
        size,
        setSize,
        mutate: mutateMedias,
        error: mediasError
    } = useSWRInfinite(
        (pageIndex, previousPageData) =>
            isFirstStatsLoaded
                ? getKey(
                      pageIndex,
                      previousPageData,
                      activeSPID,
                      limit,
                      requestParams,
                      endpoint,
                      competitorID,
                      isCompetitorRequest,
                      groupID
                  )
                : null,
        ([url, params]) => get(url, JSON.parse(params)).then((res) => res.data),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );

    // Only show loading if we have valid IDs and are actually fetching
    // If we don't have valid IDs, we're not loading (no request will be made)
    const isLoadingInitialData =
        hasValidID(groupID, activeSPID, competitorID) && !dataMedias && !mediasError;
    const isLoadingMore = size > 0 && dataMedias && typeof dataMedias[size - 1] === 'undefined';

    return {
        dataMedias,
        loadMoreMedia: () => setSize(size + 1), // Function to load more media (load more button)
        mutateMedias,
        mediasError,
        requestParams,
        setRequestParams, // Setter to change parameters in the url
        isLoadingInitialData, // Check if it's the initial loading (not the load more)
        isLoadMore:
            dataMedias &&
            (typeof dataMedias[dataMedias.length - 1].cursor === 'string' ||
                typeof dataMedias[dataMedias.length - 1].next_page_url === 'string'), // Check if there is a cursor or next_page_url to active the load more button
        isLoadingMore // Check if the load more is loading
    };
};

export default useMedias;
